import Keycloak from "keycloak-js";

// Keycloak configuration
const keycloakConfig = {
  url: import.meta.env.VITE_KEYCLOAK_URL,
  realm: import.meta.env.VITE_KEYCLOAK_REALM,
  clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID,
};

// Initialize Keycloak instance
const keycloak = new Keycloak(keycloakConfig);

export interface KeycloakUser {
  id: string;
  username: string;
  email: string;
  firstName?: string;
  lastName?: string;
  roles: string[];
}

export class KeycloakService {
  private static instance: KeycloakService;
  private keycloak: Keycloak;
  private initialized = false;

  private constructor() {
    this.keycloak = keycloak;
  }

  public static getInstance(): KeycloakService {
    if (!KeycloakService.instance) {
      KeycloakService.instance = new KeycloakService();
    }
    return KeycloakService.instance;
  }

  /**
   * Check if browser is Safari
   */
  private isSafari(): boolean {
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  }

  /**
   * Initialize with Safari-specific fallback
   */
  public async initWithFallback(): Promise<boolean> {
    try {
      return await this.init();
    } catch (error) {
      if (this.isSafari()) {
        console.warn("Safari detected, falling back to login-required mode");
        // Reset initialization state
        this.initialized = false;

        // Try with login-required instead
        try {
          const authenticated = await this.keycloak.init({
            onLoad: "login-required",
            pkceMethod: "S256",
            checkLoginIframe: false,
          });

          this.initialized = true;

          if (authenticated) {
            this.setupTokenRefresh();
          }

          return authenticated;
        } catch (fallbackError) {
          console.error(
            "Safari fallback initialization failed:",
            fallbackError,
          );
          this.initialized = true;
          return false;
        }
      } else {
        throw error;
      }
    }
  }

  /**
   * Initialize Keycloak
   */
  public async init(): Promise<boolean> {
    if (this.initialized) {
      return this.keycloak.authenticated || false;
    }

    try {
      // Detect Safari browser
      const isSafari = /^((?!chrome|android).)*safari/i.test(
        navigator.userAgent,
      );

      let initOptions;

      if (isSafari) {
        // Safari-specific configuration - use check-sso but with timeout handling
        initOptions = {
          onLoad: "check-sso", // Changed from login-required to check-sso
          pkceMethod: "S256",
          checkLoginIframe: false,
          enableLogging: true,
          // Short timeout for Safari to fail quickly if iframe doesn't work
          messageReceiveTimeout: 3000,
          // Don't use silent SSO redirect for Safari
          silentCheckSsoFallback: false,
        };
      } else {
        // Standard configuration for other browsers
        initOptions = {
          onLoad: "check-sso",
          silentCheckSsoRedirectUri:
            window.location.origin + "/silent-check-sso.html",
          pkceMethod: "S256",
          checkLoginIframe: false,
          silentCheckSsoFallback: true,
          messageReceiveTimeout: 10000,
          enableLogging: true,
        };
      }

      const authenticated = await this.keycloak.init(initOptions);

      this.initialized = true;

      // Set up token refresh
      if (authenticated) {
        this.setupTokenRefresh();
      }

      return authenticated;
    } catch (error) {
      console.error("Failed to initialize Keycloak:", error);

      // If Safari and initialization failed due to timeout, try without silent check
      if (this.isSafari() && error.message?.includes("timeout")) {
        console.warn("Safari timeout detected, retrying without iframe check");
        try {
          const authenticated = await this.keycloak.init({
            onLoad: "check-sso",
            pkceMethod: "S256",
            checkLoginIframe: false,
            enableLogging: true,
            // Completely disable all iframe-related features
            silentCheckSsoFallback: false,
            messageReceiveTimeout: 1000,
          });

          this.initialized = true;

          if (authenticated) {
            this.setupTokenRefresh();
          }

          return authenticated;
        } catch (retryError) {
          console.error("Safari retry failed:", retryError);
        }
      }

      this.initialized = true;
      return false;
    }
  }

  /**
   * Login user
   */
  public async login(): Promise<void> {
    if (!this.initialized) {
      await this.init();
    }

    // Check if already authenticated
    if (this.keycloak.authenticated) {
      console.log("User already authenticated, skipping login");
      return;
    }

    try {
      await this.keycloak.login({
        redirectUri: window.location.href,
      });
    } catch (error) {
      console.error("Login failed:", error);
      throw new Error("Login failed");
    }
  }

  /**
   * Logout user
   */
  public async logout(): Promise<void> {
    try {
      await this.keycloak.logout({
        redirectUri: window.location.origin,
      });
    } catch (error) {
      console.error("Logout failed:", error);
      throw new Error("Logout failed");
    }
  }

  /**
   * Register user
   */
  public async register(): Promise<void> {
    if (!this.initialized) {
      await this.init();
    }

    try {
      await this.keycloak.register({
        redirectUri: window.location.origin,
      });
    } catch (error) {
      console.error("Registration failed:", error);
      throw new Error("Registration failed");
    }
  }

  /**
   * Check if user is authenticated
   */
  public isAuthenticated(): boolean {
    return this.keycloak.authenticated || false;
  }

  /**
   * Force refresh authentication state
   */
  public async refreshAuthState(): Promise<boolean> {
    try {
      // Try to update token to verify current authentication state
      const refreshed = await this.updateToken(30);
      return this.keycloak.authenticated || false;
    } catch (error) {
      console.error("Failed to refresh auth state:", error);
      return false;
    }
  }

  /**
   * Get authentication status with token validation
   */
  public async getAuthStatus(): Promise<{
    authenticated: boolean;
    user: KeycloakUser | null;
    tokenValid: boolean;
  }> {
    const authenticated = this.isAuthenticated();
    let tokenValid = false;

    if (authenticated) {
      try {
        tokenValid = await this.updateToken(30);
      } catch (error) {
        console.error("Token validation failed:", error);
      }
    }

    return {
      authenticated,
      user: this.getUser(),
      tokenValid,
    };
  }

  /**
   * Get access token
   */
  public getToken(): string | undefined {
    const token = this.keycloak.token;
    const authenticated = this.keycloak.authenticated;

    console.log("🔍 [Keycloak] getToken called");
    console.log("🔍 [Keycloak] Initialized:", this.initialized);
    console.log("🔍 [Keycloak] Authenticated:", authenticated);
    console.log("🔍 [Keycloak] Token exists:", !!token);

    if (!authenticated) {
      console.warn("⚠️ [Keycloak] User is NOT authenticated!");
    }

    if (!token && authenticated) {
      console.error("❌ [Keycloak] Authenticated but no token available!");
    }

    return token;
  }

  /**
   * Get refresh token
   */
  public getRefreshToken(): string | undefined {
    return this.keycloak.refreshToken;
  }

  /**
   * Get user info
   */
  public getUser(): KeycloakUser | null {
    if (!this.keycloak.authenticated || !this.keycloak.tokenParsed) {
      return null;
    }

    const tokenParsed = this.keycloak.tokenParsed as any;
    const realmAccess = this.keycloak.realmAccess;

    return {
      id: tokenParsed.sub,
      username: tokenParsed.preferred_username || tokenParsed.email,
      email: tokenParsed.email,
      firstName: tokenParsed.given_name,
      lastName: tokenParsed.family_name,
      roles: realmAccess?.roles || [],
    };
  }

  /**
   * Check if user has role
   */
  public hasRole(role: string): boolean {
    return this.keycloak.hasRealmRole(role);
  }

  /**
   * Check if user has any of the provided roles
   */
  public hasAnyRole(roles: string[]): boolean {
    return roles.some((role) => this.hasRole(role));
  }

  /**
   * Update token if needed
   */
  public async updateToken(minValidity = 30): Promise<boolean> {
    try {
      return await this.keycloak.updateToken(minValidity);
    } catch (error) {
      console.error("Failed to update token:", error);
      return false;
    }
  }

  /**
   * Get user profile from Keycloak
   */
  public async loadUserProfile(): Promise<any> {
    try {
      return await this.keycloak.loadUserProfile();
    } catch (error) {
      console.error("Failed to load user profile:", error);
      return null;
    }
  }

  /**
   * Setup automatic token refresh
   */
  private setupTokenRefresh(): void {
    // Refresh token every 30 seconds if it expires in less than 60 seconds
    setInterval(async () => {
      if (this.keycloak.authenticated) {
        try {
          await this.updateToken(60);
        } catch (error) {
          console.error("Token refresh failed:", error);
          // Could trigger re-authentication here
        }
      }
    }, 30000);

    // Set up Keycloak events
    this.keycloak.onTokenExpired = () => {
      console.log("Token expired");
      this.updateToken(30).catch((error) => {
        console.error("Token refresh failed:", error);
      });
    };

    this.keycloak.onAuthSuccess = () => {
      console.log("Authentication successful");
    };
    this.keycloak.onAuthError = (error: any) => {
      console.error("Authentication error:", error);
    };

    this.keycloak.onAuthRefreshSuccess = () => {
      console.log("Token refreshed successfully");
    };

    this.keycloak.onAuthRefreshError = () => {
      console.error("Token refresh error");
    };

    this.keycloak.onAuthLogout = () => {
      console.log("User logged out");
    };
  }

  /**
   * Get Keycloak instance (for advanced usage)
   */
  public getKeycloakInstance(): Keycloak {
    return this.keycloak;
  }
}

// Export singleton instance
export const keycloakService = KeycloakService.getInstance();
